import { Onfido } from "onfido-sdk-ui";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { setupOnfidoConfigurations } from "../actions";
import { OnfidoState, setOnfidoState as setState } from "../reducer";
import { Handle, SdkParameters } from "onfido-sdk-ui/types";
import { TOnfidoAnalyticsEvent, TOnfidoParams } from "../types";
import { useConfig } from "./useConfig";

export type TUseOnfidoConfig = {
  params?: TOnfidoParams;
  containerRef: RefObject<Element>;
  analyticsHandler?: (evt: TOnfidoAnalyticsEvent) => void;
  onComplete?: SdkParameters["onComplete"];
  onError?: SdkParameters["onError"];
};

export type TUseOnfido = (C: TUseOnfidoConfig) => void;

export const useOnfido: TUseOnfido = ({
  params,
  containerRef,
  analyticsHandler,
  onComplete,
  onError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const instance = useRef<Handle | null>(null);
  const config = useConfig({
    containerRef,
    onError,
    onComplete,
  });

  const setOnfidoState = useCallback(
    (payload: Partial<OnfidoState>) => {
      dispatch(setState(payload));
    },
    [dispatch],
  );

  const checkInstanceAndTearDown = useCallback(async () => {
    if (instance.current !== null) {
      await instance.current.tearDown();
      instance.current = null;
    }
  }, []);

  const initOnfido = useCallback(async () => {
    await checkInstanceAndTearDown();
    if (config) {
      instance.current = Onfido.init({
        steps: config.steps,
        token: config.token,
        containerId: config.containerId,
        onComplete: config.onComplete,
        onError: config.onError,
        theme: config.customUI,
        language: config.language,
        smsNumberCountryCode: config.smsNumberCountryCode,
        version: "14.62.0", // TODO: find a way to make this dynamic
      });
    }
  }, [checkInstanceAndTearDown, config]);

  // Onfido init effect
  useEffect(() => {
    initOnfido();
    return () => {
      checkInstanceAndTearDown();
    };
  }, [checkInstanceAndTearDown, initOnfido]);

  // Setup and save phases effect
  useEffect(() => {
    if (params) {
      dispatch(setupOnfidoConfigurations(params));
    }
  }, [dispatch, params]);

  const analyticsListener = useCallback(
    (evt: TOnfidoAnalyticsEvent) => {
      setOnfidoState({
        subStep: evt.detail?.eventName,
        isCrossDevice: evt.detail?.isCrossDevice,
      });
      analyticsHandler?.(evt);
    },
    [analyticsHandler, setOnfidoState],
  );

  // Analytics effect
  useEffect(() => {
    window.addEventListener("userAnalyticsEvent", analyticsListener);
    return () =>
      window.removeEventListener("userAnalyticsEvent", analyticsListener);
  }, [analyticsListener]);
};
